<template>
  <v-container class="help-center-container">
    <!-- Estado 1: Centro de Ayuda (inicio) -->
    <div v-if="currentState === 'hub'" class="help-hub">
      <div class="help-header mb-6">
        <h1 class="display-1 font-weight-bold mb-2">Centro de Ayuda</h1>
        <p class="subheading grey--text mb-0">Encuentra respuestas rápidas o crea un ticket de soporte</p>
        <v-btn
          flat
          dark
          small
          class="mt-3 text-none font-weight-bold"
          @click="goToMyTickets"
        >
          <v-icon left small>confirmation_number</v-icon>
          Ver Mis Tickets
        </v-btn>
      </div>

      <!-- Tabs: Manuales y FAQ -->
      <v-layout row wrap>
        <v-flex xs12>
          <v-tabs v-model="hubTab" color="primary" slider-color="primary" class="help-tabs">
            <v-tab href="#manuales" class="font-weight-bold tab-label">
              <v-icon left small>school</v-icon>
              Manuales
            </v-tab>
            <v-tab href="#faq" class="font-weight-bold tab-label">
              <v-icon left small>help</v-icon>
              Preguntas Frecuentes
            </v-tab>

            <!-- Tab: Manuales -->
            <v-tab-item value="manuales" id="manuales" class="tab-content">
              <v-layout row wrap class="mb-6">
                <v-flex xs12>
                  <v-text-field
                    v-model="searchQuery"
                    placeholder="Buscar en manuales..."
                    prepend-icon="search"
                    style="max-width: 100%;"
                    @input="filterManuals"
                    outlined
                    rounded
                    dense
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-layout row wrap v-if="filteredManuals.length > 0">
                <v-flex xs12>
                  <h3 class="subheading font-weight-bold mb-4">Manuales Disponibles</h3>
                  <v-list two-line class="manual-list-container">
                    <v-list-tile
                      v-for="(manual, idx) in filteredManuals"
                      :key="idx"
                      @click="startReadingManual(manual)"
                      class="manual-list-item"
                    >
                      <v-list-tile-avatar>
                        <v-icon :color="manual.color">{{ manual.icon }}</v-icon>
                      </v-list-tile-avatar>
                      <v-list-tile-content>
                        <v-list-tile-title class="font-weight-bold">{{ manual.title }}</v-list-tile-title>
                        <v-list-tile-sub-title>{{ manual.description }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </v-list>
                </v-flex>
              </v-layout>

              <v-layout row wrap v-else class="py-6">
                <v-flex xs12 class="text-center">
                  <v-icon size="48" color="grey lighten-2" class="mb-3">find_in_page</v-icon>
                  <p class="grey--text">No se encontraron manuales que coincidan con tu búsqueda.</p>
                </v-flex>
              </v-layout>
            </v-tab-item>

            <!-- Tab: FAQ -->
            <v-tab-item value="faq" id="faq" class="tab-content">
              <v-layout row wrap class="mb-6">
                <v-flex xs12>
                  <v-text-field
                    v-model="faqSearchQuery"
                    placeholder="Buscar preguntas frecuentes..."
                    prepend-icon="search"
                    style="max-width: 100%;"
                    outlined
                    rounded
                    dense
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-layout row wrap v-if="filteredFaqs.length > 0">
                <v-flex xs12>
                  <h3 class="subheading font-weight-bold mb-4">Preguntas Frecuentes</h3>
                  <v-list two-line class="manual-list-container faq-list-container">
                    <v-list-group
                      v-for="(faq, idx) in filteredFaqs"
                      :key="idx"
                      no-action
                      class="manual-list-item faq-list-item"
                    >
                      <v-list-tile slot="activator">
                        <v-list-tile-avatar>
                          <v-icon color="primary">{{ faq.icon }}</v-icon>
                        </v-list-tile-avatar>
                        <v-list-tile-content>
                          <v-list-tile-title class="font-weight-bold">{{ faq.question }}</v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>

                      <v-list-tile class="faq-answer-tile">
                        <v-list-tile-content>
                          <p class="body-1 faq-answer-text mb-0">{{ faq.answer }}</p>
                          <div v-if="faq.tips" class="faq-tips mt-3">
                            <strong class="faq-tips-label">
                              <v-icon small color="#1976d2" class="mr-1">lightbulb_outline</v-icon>
                              Tips:
                            </strong>
                            <p class="caption mb-0 mt-1">{{ faq.tips }}</p>
                          </div>
                        </v-list-tile-content>
                      </v-list-tile>
                    </v-list-group>
                  </v-list>
                </v-flex>
              </v-layout>

              <v-layout row wrap v-else class="py-6">
                <v-flex xs12 class="text-center">
                  <v-icon size="48" color="grey lighten-2" class="mb-3">help_outline</v-icon>
                  <p class="grey--text">No se encontraron preguntas que coincidan con tu búsqueda.</p>
                </v-flex>
              </v-layout>
            </v-tab-item>
          </v-tabs>
        </v-flex>
      </v-layout>
    </div>

    <!-- Estado 2: Leyendo Manual -->
    <div v-if="currentState === 'reading-manual'" class="reading-state">
      <!-- Toolbar -->
      <v-toolbar flat class="mb-4" style="background: transparent;">
        <v-btn icon @click="currentState = 'hub'">
          <v-icon>arrow_back</v-icon>
        </v-btn>
        <v-toolbar-title class="font-weight-bold">{{ readingManual.title }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          v-if="numPages > 0"
          icon
          @click="printPdf"
          title="Imprimir"
        >
          <v-icon>print</v-icon>
        </v-btn>
        <v-btn
          v-if="readingManual.link !== '#'"
          icon
          :href="readingManual.link"
          target="_blank"
          title="Abrir en nueva pestaña"
        >
          <v-icon>open_in_new</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- PDF Viewer -->
      <div class="pdf-viewer-container">
        <div v-if="loadingPdf" class="loading-state">
          <v-progress-circular indeterminate color="primary" size="64" class="mb-4"></v-progress-circular>
          <span class="subheading grey--text">Cargando documento...</span>
        </div>

        <div v-else-if="!readingManual.link || readingManual.link === '#'" class="not-available-state">
          <v-icon size="88" color="grey lighten-2" class="mb-5">construction</v-icon>
          <h3 class="headline font-weight-bold grey--text text--darken-2 text-center">Documento en Construcción</h3>
          <p class="body-1 grey--text text--darken-1 mt-3 text-center">Este manual se encuentra en proceso de elaboración.</p>
        </div>

        <div v-else class="pdf-content">
          <pdf ref="myPdfComponent" style="display: none;" :src="pdfSrc"></pdf>
          <div v-for="i in numPages" :key="i" class="pdf-page-wrapper">
            <pdf :src="pdfSrc" :page="i" style="width: 100%"></pdf>
          </div>
        </div>
      </div>

      <!-- Resolution Panel -->
      <v-card class="mt-6 elevation-2" style="background: #f5f5f5; border-radius: 12px;">
        <v-card-text class="pa-6">
          <h3 class="subheading font-weight-bold mb-4">¿Se resolvió tu problema?</h3>
          <div class="resolution-buttons">
            <v-btn
              color="success"
              large
              outline
              @click="resolvedProblem"
              class="font-weight-bold text-none mr-3"
              style="border-radius: 8px; min-width: 120px;"
            >
              <v-icon left>check_circle</v-icon>
              Sí, se resolvió
            </v-btn>
            <v-btn
              color="error"
              large
              outline
              @click="goToCreateTicket"
              class="font-weight-bold text-none"
              style="border-radius: 8px; min-width: 120px;"
            >
              <v-icon left>cancel</v-icon>
              No, crear ticket
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Estado 3: Crear Ticket -->
    <div v-if="currentState === 'create-ticket'" class="create-ticket-state">
      <v-toolbar flat class="mb-4" style="background: transparent;">
        <v-btn icon @click="currentState = 'hub'">
          <v-icon>arrow_back</v-icon>
        </v-btn>
        <v-toolbar-title class="font-weight-bold">Crear Ticket de Soporte</v-toolbar-title>
      </v-toolbar>

      <v-layout row wrap>
        <!-- Main form -->
        <v-flex xs12 md8>
          <v-card class="elevation-1">
            <v-card-text class="pa-6">
              <v-form ref="ticketForm">
                <v-text-field
                  v-model="ticketForm.title"
                  label="Título del problema *"
                  placeholder="Ej: No puedo conectarme al WiFi"
                  prepend-icon="edit"
                  :error-messages="errores.title"
                  counter="100"
                  maxlength="100"
                  class="mb-2"
                  @input="updateRelatedManuals"
                  required
                ></v-text-field>

                <v-textarea
                  v-model="ticketForm.description"
                  label="Descripción detallada *"
                  placeholder="Describe lo que intentaste, qué sucedió y cualquier mensaje de error..."
                  prepend-icon="notes"
                  :error-messages="errores.description"
                  rows="8"
                  counter="2000"
                  maxlength="2000"
                  class="mb-2"
                  @input="updateRelatedManuals"
                  required
                ></v-textarea>

                <v-select
                  v-model="ticketForm.area_id"
                  label="Área *"
                  placeholder="Selecciona el área correspondiente"
                  :items="areas"
                  item-text="name"
                  item-value="id"
                  prepend-icon="folder"
                  :error-messages="errores.area_id"
                  :loading="loadingAreas"
                  class="mb-2"
                  required
                ></v-select>

                <v-text-field
                  v-model="ticketForm.client_name"
                  label="Nombre del cliente"
                  prepend-icon="person"
                  :error-messages="errores.client_name"
                  readonly
                  class="mb-2"
                ></v-text-field>

                <v-text-field
                  v-model="ticketForm.client_email"
                  type="email"
                  label="Email"
                  prepend-icon="email"
                  :error-messages="errores.client_email"
                  readonly
                  class="mb-2"
                ></v-text-field>

                <v-text-field
                  v-model="ticketForm.client_phone"
                  label="Teléfono"
                  prepend-icon="phone"
                  :error-messages="errores.client_phone"
                  readonly
                  class="mb-6"
                ></v-text-field>

                <v-alert :value="!!errorAlert" type="error" outline class="mb-4">
                  {{ errorAlert }}
                </v-alert>

                <v-btn
                  color="primary"
                  large
                  block
                  :loading="submittingTicket"
                  class="font-weight-bold text-none"
                  style="border-radius: 8px; height: 48px;"
                  @click="submitTicket"
                >
                  <v-icon left>send</v-icon>
                  Enviar Ticket
                </v-btn>
              </v-form>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Related manuals sidebar -->
        <v-flex xs12 md4>
          <v-card class="elevation-1" style="background: #f0f8ff; border-left: 4px solid #2196F3; border-radius: 8px;">
            <v-card-text class="pa-6">
              <h4 class="subheading font-weight-bold mb-3 related-manuals-title">
                <v-icon small color="#2196F3">lightbulb_outline</v-icon>
                Posibles soluciones
              </h4>

              <div v-if="relatedManuals.length === 0" class="text-center py-6">
                <p class="caption grey--text">Escribe tu problema para ver manuales relacionados...</p>
              </div>

              <div v-else>
                <v-list dense>
                  <v-list-tile
                    v-for="(manual, idx) in relatedManuals"
                    :key="idx"
                    @click="startReadingManual(manual)"
                    class="related-manual-item"
                  >
                    <v-list-tile-avatar style="min-width: 32px;">
                      <v-icon small :color="manual.color">{{ manual.icon }}</v-icon>
                    </v-list-tile-avatar>
                    <v-list-tile-content>
                      <v-list-tile-title class="caption font-weight-bold">
                        {{ manual.title }}
                      </v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </v-list>

                <v-divider class="my-3"></v-divider>
                <p class="caption grey--text">Haz clic en un manual para revisar antes de enviar el ticket</p>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </div>

    <!-- Estado 4: Mis Tickets -->
    <div v-if="currentState === 'my-tickets'" class="my-tickets-state">
      <v-toolbar flat class="mb-4" style="background: transparent;">
        <v-btn icon @click="currentState = 'hub'">
          <v-icon>arrow_back</v-icon>
        </v-btn>
        <v-toolbar-title class="font-weight-bold">Mis Tickets</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="loadTickets" :loading="loadingTickets" title="Actualizar">
          <v-icon>refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-layout row wrap class="mb-4">
        <v-flex xs12>
          <v-btn
            color="primary"
            @click="goToCreateTicket"
            class="font-weight-bold text-none"
            style="border-radius: 8px;"
          >
            <v-icon left>add_circle</v-icon>
            Nuevo Ticket
          </v-btn>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="tickets.length > 0">
        <v-flex xs12>
          <v-list two-line>
            <v-list-tile
              v-for="ticket in tickets"
              :key="ticket.id"
              @click="openTicketDetails(ticket)"
              class="ticket-list-item"
            >
              <v-list-tile-content>
                <v-list-tile-title class="font-weight-bold">{{ ticket.folio }} — {{ ticket.title }}</v-list-tile-title>
                <v-list-tile-sub-title>{{ formatDate(ticket.created_date) }}</v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-chip :color="getStatusColor(ticket.status)" text-color="white" small>
                  {{ ticket.status }}
                </v-chip>
              </v-list-tile-action>
            </v-list-tile>
          </v-list>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-else>
        <v-flex xs12 class="text-xs-center py-6">
          <v-progress-circular v-if="loadingTickets" indeterminate color="primary" size="48"></v-progress-circular>
          <div v-else>
            <v-icon size="64" color="grey lighten-1" class="mb-3">inbox</v-icon>
            <p class="grey--text">No tienes tickets registrados.</p>
          </div>
        </v-flex>
      </v-layout>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════════════ -->
    <!-- ── MODAL PRINCIPAL: DETALLES COMPLETOS DEL TICKET ── -->
    <!-- ═══════════════════════════════════════════════════════════════════════ -->
    <v-dialog v-model="ticketDetailsDialog" max-width="850" scrollable>
      <v-card v-if="selectedTicket" class="ticket-detail-modal-card">
        <!-- Toolbar Superior -->
        <v-toolbar flat dark color="#002b48" class="ticket-modal-toolbar">
          <v-icon left>confirmation_number</v-icon>
          <v-toolbar-title class="font-weight-bold">
            Ticket #{{ selectedTicket.folio || selectedTicket.id }}
          </v-toolbar-title>
          <v-chip :color="getStatusColor(selectedTicket.status)" text-color="white" small class="ml-3 font-weight-bold">
            {{ selectedTicket.status }}
          </v-chip>
          <v-chip v-if="selectedTicket.is_hardware" color="teal darken-1" text-color="white" small class="ml-2 font-weight-bold">
            <v-icon left small>precision_manufacturing</v-icon>
            Hardware
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="ticketDetailsDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <!-- Cabecera / Hero del Ticket -->
        <div class="ticket-hero-banner pa-4">
          <h2 class="headline font-weight-bold mb-2">{{ selectedTicket.title }}</h2>
          <div class="ticket-hero-meta">
            <span class="meta-item"><v-icon small class="mr-1">place</v-icon> {{ selectedTicket.area || 'General' }}</span>
            <span class="meta-item" v-if="selectedTicket.client_company"><v-icon small class="mr-1">business</v-icon> <strong>{{ selectedTicket.client_company }}</strong></span>
            <span class="meta-item" v-if="selectedTicket.client_name"><v-icon small class="mr-1">person</v-icon> {{ selectedTicket.client_name }}</span>
            <span class="meta-item"><v-icon small class="mr-1">schedule</v-icon> {{ formatDate(selectedTicket.created_date) }}</span>
          </div>
        </div>

        <!-- Pestañas de Navegación del Ticket (SOLO si está vinculado a Hardware) -->
        <v-tabs 
          v-if="selectedTicket.is_hardware" 
          v-model="detailTab" 
          dark 
          color="#002b48" 
          slider-color="#2196F3" 
          class="detail-modal-tabs"
        >
          <v-tab href="#tab-info" class="font-weight-bold text-none tab-item-white">
            <v-icon left small>description</v-icon>
            Información General
          </v-tab>
          <v-tab href="#tab-tech" class="font-weight-bold text-none tab-item-white">
            <v-icon left small>engineering</v-icon>
            Revisión Técnica
          </v-tab>
          <v-tab href="#tab-quote" class="font-weight-bold text-none tab-item-white">
            <v-icon left small>request_quote</v-icon>
            Cotización & Aprobación
            <v-badge v-if="selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.directorQuote && selectedTicket.hardware_metadata.directorQuote.clientStatus === 'pendiente_cliente'" color="warning" inline>
              <template v-slot:badge>1</template>
            </v-badge>
          </v-tab>
        </v-tabs>

        <!-- Contenido de las Pestañas -->
        <v-card-text style="max-height: 580px;" class="pa-4 bg-light-gray">
          <!-- Si es ticket de hardware: mostramos las pestañas -->
          <v-tabs-items v-if="selectedTicket.is_hardware" v-model="detailTab">

            <!-- ── PESTAÑA 1: INFORMACIÓN GENERAL & ACTIVO ── -->
            <v-tab-item value="tab-info">
              <v-card class="elevation-1 pa-4 mb-3 border-radius-8">
                <h4 class="subheading font-weight-bold mb-3 primary--text">
                  <v-icon small color="primary" class="mr-1">assignment</v-icon>
                  Descripción del Problema
                </h4>
                <p class="body-1 text-pre-wrap mb-0">{{ cleanTicketDescription(selectedTicket) }}</p>
              </v-card>

              <!-- Tarjeta de Hardware Asociado si aplica -->
              <v-card v-if="selectedTicket.is_hardware || (selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.equipmentName)" class="elevation-1 pa-4 mb-3 hw-card border-radius-8">
                <div class="d-flex align-center justify-space-between mb-3">
                  <h4 class="subheading font-weight-bold teal--text text--darken-3 mb-0">
                    <v-icon small color="teal darken-3" class="mr-1">dns</v-icon>
                    Activo de Hardware Vinculado
                  </h4>
                  <v-chip small outline color="teal darken-2">
                    {{ selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.equipmentCode ? selectedTicket.hardware_metadata.equipmentCode : 'Activo Registrado' }}
                  </v-chip>
                </div>

                <v-layout row wrap class="hw-spec-grid">
                  <v-flex xs12 sm6 class="mb-2">
                    <small class="grey--text">Equipo / Dispositivo</small>
                    <p class="body-1 font-weight-bold mb-0">{{ (selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.equipmentName) || selectedTicket.title }}</p>
                  </v-flex>
                  <v-flex xs12 sm6 class="mb-2">
                    <small class="grey--text">Empresa / Ubicación</small>
                    <p class="body-1 mb-0">{{ selectedTicket.client_company || 'Manufactura Global' }}</p>
                  </v-flex>
                  <v-flex xs12 sm6 class="mb-2" v-if="selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.lastMaintenanceDate">
                    <small class="grey--text">Último Mantenimiento</small>
                    <p class="body-2 mb-0">{{ selectedTicket.hardware_metadata.lastMaintenanceDate }}</p>
                  </v-flex>
                  <v-flex xs12 sm6 class="mb-2" v-if="selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.nextMaintenanceDate">
                    <small class="grey--text">Próximo Servicio Programado</small>
                    <p class="body-2 font-weight-bold teal--text text--darken-2 mb-0">{{ selectedTicket.hardware_metadata.nextMaintenanceDate }}</p>
                  </v-flex>
                </v-layout>

                <!-- Estado de Póliza / Cargo -->
                <div class="policy-notice-box mt-3 pa-3" :class="isAdditionalChargeRequired() ? 'with-charge' : 'no-charge'">
                  <div class="d-flex align-center">
                    <v-icon small :color="isAdditionalChargeRequired() ? 'orange darken-3' : 'green darken-2'" class="mr-2">
                      {{ isAdditionalChargeRequired() ? 'monetization_on' : 'verified' }}
                    </v-icon>
                    <strong>
                      {{ isAdditionalChargeRequired() ? 'Aplica Cargo Adicional (Cotización Requerida)' : 'Servicio Cubierto por Póliza de Mantenimiento' }}
                    </strong>
                  </div>
                  <p class="caption grey--text text--darken-2 mb-0 mt-1">
                    {{ isAdditionalChargeRequired() 
                      ? 'La revisión técnica en sitio determinó requerimiento de cotización adicional para refacciones o trabajos extraordinarios.' 
                      : 'Servicio ordinario cubierto bajo la póliza y contrato de soporte del sistema.' }}
                  </p>
                </div>
              </v-card>

              <!-- Tarjeta de Contacto & Asignación -->
              <v-card class="elevation-1 pa-4 border-radius-8">
                <h4 class="subheading font-weight-bold mb-3 primary--text">
                  <v-icon small color="primary" class="mr-1">info</v-icon>
                  Detalles de Atención
                </h4>
                <v-layout row wrap>
                  <v-flex xs12 sm6 class="mb-3">
                    <small class="grey--text">Responsable Técnico</small>
                    <p class="body-1 mb-0">{{ selectedTicket.responsable || 'Sin asignar' }}</p>
                  </v-flex>
                  <v-flex xs12 sm6 class="mb-3">
                    <small class="grey--text">Área Canalizada</small>
                    <p class="body-1 mb-0">{{ selectedTicket.area || 'General' }}</p>
                  </v-flex>
                  <v-flex xs12 sm6 class="mb-3" v-if="selectedTicket.client_email">
                    <small class="grey--text">Correo del Solicitante</small>
                    <p class="body-2 mb-0">{{ selectedTicket.client_email }}</p>
                  </v-flex>
                  <v-flex xs12 sm6 class="mb-3" v-if="selectedTicket.client_phone">
                    <small class="grey--text">Teléfono de Contacto</small>
                    <p class="body-2 mb-0">{{ selectedTicket.client_phone }}</p>
                  </v-flex>
                </v-layout>
              </v-card>
            </v-tab-item>

            <!-- ── PESTAÑA 2: REVISIÓN TÉCNICA EN SITIO ── -->
            <v-tab-item value="tab-tech">
              <!-- Si existe Reporte Técnico -->
              <div v-if="selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.technicalReview">
                <v-card class="elevation-1 pa-4 mb-3 border-radius-8">
                  <div class="d-flex align-center justify-space-between flex-wrap mb-3">
                    <div>
                      <h4 class="subheading font-weight-bold mb-0">Hoja de Revisión Técnica en Sitio</h4>
                      <small class="grey--text">Realizada por {{ selectedTicket.hardware_metadata.technicalReview.technicianName }} el {{ selectedTicket.hardware_metadata.technicalReview.reviewDate }}</small>
                    </div>
                    <v-chip :color="getEquipmentConditionColor(selectedTicket.hardware_metadata.technicalReview.equipmentCondition)" text-color="white" small class="font-weight-bold">
                      Estado: {{ selectedTicket.hardware_metadata.technicalReview.equipmentCondition }}
                    </v-chip>
                  </div>

                  <!-- Diagnóstico y Hallazgos -->
                  <div class="findings-box pa-3 mb-3">
                    <small class="font-weight-bold teal--text text--darken-3">Diagnóstico y Pruebas Realizadas:</small>
                    <p class="body-1 text-pre-wrap mb-0 mt-1">{{ selectedTicket.hardware_metadata.technicalReview.findings }}</p>
                  </div>

                  <!-- Galería de Evidencias Fotográficas -->
                  <div v-if="selectedTicket.hardware_metadata.technicalReview.images && selectedTicket.hardware_metadata.technicalReview.images.length > 0" class="evidence-gallery-section mb-3">
                    <h5 class="body-2 font-weight-bold mb-2 grey--text text--darken-2">
                      <v-icon small class="mr-1">photo_camera</v-icon>
                      Evidencias Fotográficas en Sitio ({{ selectedTicket.hardware_metadata.technicalReview.images.length }}):
                    </h5>
                    <div class="evidence-grid">
                      <div 
                        v-for="(img, idx) in selectedTicket.hardware_metadata.technicalReview.images" 
                        :key="idx"
                        class="evidence-thumb-wrap"
                        @click="previewPhoto(img)"
                      >
                        <img :src="img" alt="Evidencia en sitio" class="evidence-thumb" />
                        <div class="thumb-overlay">
                          <v-icon dark small>zoom_in</v-icon>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Notas del Técnico -->
                  <div v-if="selectedTicket.hardware_metadata.technicalReview.technicianNotes" class="tech-notes-card pa-3">
                    <small class="font-weight-bold grey--text text--darken-3">Recomendaciones técnicas adicionales:</small>
                    <p class="body-2 grey--text text--darken-2 mb-0 mt-1">{{ selectedTicket.hardware_metadata.technicalReview.technicianNotes }}</p>
                  </div>
                </v-card>
              </div>

              <!-- Si aún no hay reporte técnico -->
              <div v-else class="text-xs-center py-6">
                <v-icon size="64" color="grey lighten-1" class="mb-3">pending_actions</v-icon>
                <h3 class="title font-weight-bold grey--text text--darken-2 mb-1">Revisión Técnica en Sitio Pendiente</h3>
                <p class="body-2 grey--text mb-0" style="max-width: 460px; margin: 0 auto;">
                  El técnico asignado acudirá a las instalaciones para realizar la inspección física, pruebas operativas y capturar el diagnóstico correspondiente.
                </p>
              </div>
            </v-tab-item>

            <!-- ── PESTAÑA 3: COTIZACIÓN & APROBACIÓN ── -->
            <v-tab-item value="tab-quote">
              <!-- Si existe Cotización del Director Técnico -->
              <div v-if="selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.directorQuote">
                <v-card class="elevation-1 pa-4 mb-3 border-radius-8">
                  <div class="d-flex align-center justify-space-between flex-wrap mb-3">
                    <div>
                      <h4 class="subheading font-weight-bold mb-0">Cotización Oficial de Trabajo</h4>
                      <small class="grey--text">
                        Folio: <strong>{{ selectedTicket.hardware_metadata.directorQuote.quoteFolio }}</strong> · 
                        Elaboró: {{ selectedTicket.hardware_metadata.directorQuote.preparedBy }} · 
                        Vigencia: {{ selectedTicket.hardware_metadata.directorQuote.validityDays }} días
                      </small>
                    </div>
                    <!-- Chip de Estado de Aprobación -->
                    <div class="mt-2 mt-sm-0">
                      <v-chip 
                        :color="getQuoteStatusColor(selectedTicket.hardware_metadata.directorQuote.clientStatus)" 
                        text-color="white" 
                        small 
                        class="font-weight-bold"
                      >
                        <v-icon left small>{{ getQuoteStatusIcon(selectedTicket.hardware_metadata.directorQuote.clientStatus) }}</v-icon>
                        {{ getQuoteStatusLabel(selectedTicket.hardware_metadata.directorQuote.clientStatus) }}
                      </v-chip>
                    </div>
                  </div>

                  <!-- Tabla de Partidas -->
                  <div class="quote-table-wrapper mb-3">
                    <table class="quote-table">
                      <thead>
                        <tr>
                          <th>Concepto / Partida</th>
                          <th style="width: 15%; text-align: center;">Cantidad</th>
                          <th style="width: 20%; text-align: right;">P. Unitario</th>
                          <th style="width: 20%; text-align: right;">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, idx) in selectedTicket.hardware_metadata.directorQuote.items" :key="idx">
                          <td>{{ item.concept }}</td>
                          <td style="text-align: center;">{{ item.quantity }}</td>
                          <td style="text-align: right;">{{ formatCurrency(item.unitPrice) }}</td>
                          <td style="text-align: right;" class="font-weight-bold">{{ formatCurrency(item.total) }}</td>
                        </tr>
                      </tbody>
                    </table>

                    <!-- Totales -->
                    <div class="quote-totals-card pa-3 mt-2">
                      <div class="d-flex justify-space-between mb-1">
                        <span class="grey--text">Subtotal:</span>
                        <strong>{{ formatCurrency(selectedTicket.hardware_metadata.directorQuote.subtotal) }}</strong>
                      </div>
                      <div class="d-flex justify-space-between mb-1">
                        <span class="grey--text">IVA (16%):</span>
                        <strong>{{ formatCurrency(selectedTicket.hardware_metadata.directorQuote.tax) }}</strong>
                      </div>
                      <v-divider class="my-2"></v-divider>
                      <div class="d-flex justify-space-between align-center">
                        <span class="title font-weight-bold">Total a Facturar:</span>
                        <strong class="title primary--text font-weight-bold">{{ formatCurrency(selectedTicket.hardware_metadata.directorQuote.total) }}</strong>
                      </div>
                    </div>
                  </div>

                  <!-- Panel de Acción / Estado de Aprobación por la Empresa -->
                  <div class="quote-approval-card pa-4 mt-3" :class="selectedTicket.hardware_metadata.directorQuote.clientStatus">
                    <!-- 1. Si está pendiente de aprobación -->
                    <div v-if="selectedTicket.hardware_metadata.directorQuote.clientStatus === 'pendiente_cliente'">
                      <div class="d-flex align-center mb-2">
                        <v-icon color="orange darken-2" class="mr-2">hourglass_top</v-icon>
                        <h4 class="subheading font-weight-bold orange--text text--darken-3 mb-0">
                          Esperando Autorización de la Empresa
                        </h4>
                      </div>
                      <p class="body-2 grey--text text--darken-2 mb-3">
                        Por favor revisa el desglose del presupuesto. Al autorizar la cotización, el equipo técnico será notificado inmediatamente para iniciar los trabajos.
                      </p>
                      <div class="d-flex flex-wrap" style="gap: 12px;">
                        <v-btn
                          color="success"
                          class="font-weight-bold text-none"
                          @click="openQuoteModal('aprobada')"
                        >
                          <v-icon left>check_circle</v-icon>
                          Aprobar Cotización
                        </v-btn>
                        <v-btn
                          color="error"
                          flat
                          class="font-weight-bold text-none"
                          @click="openQuoteModal('rechazada')"
                        >
                          <v-icon left>cancel</v-icon>
                          Rechazar
                        </v-btn>
                      </div>
                    </div>

                    <!-- 2. Si ya fue aprobada -->
                    <div v-else-if="selectedTicket.hardware_metadata.directorQuote.clientStatus === 'aprobada'">
                      <div class="d-flex align-center justify-space-between flex-wrap mb-2">
                        <div class="d-flex align-center">
                          <v-icon color="success" class="mr-2">verified</v-icon>
                          <h4 class="subheading font-weight-bold success--text mb-0">
                            Cotización Autorizada Exitosamente
                          </h4>
                        </div>
                        <v-btn
                          small
                          outline
                          color="error"
                          class="font-weight-bold text-none mt-1 mt-sm-0"
                          @click="openQuoteModal('rechazada')"
                        >
                          <v-icon left small>cancel</v-icon>
                          Cambiar a Rechazada
                        </v-btn>
                      </div>
                      <v-layout row wrap class="mt-2">
                        <v-flex xs12 sm4 class="mb-1" v-if="selectedTicket.hardware_metadata.directorQuote.clientApprovalDate">
                          <small class="grey--text">Fecha de Autorización</small>
                          <p class="body-2 mb-0 font-weight-bold">{{ selectedTicket.hardware_metadata.directorQuote.clientApprovalDate }}</p>
                        </v-flex>
                        <v-flex xs12 sm4 class="mb-1" v-if="selectedTicket.hardware_metadata.directorQuote.clientApprovalUser">
                          <small class="grey--text">Autorizó</small>
                          <p class="body-2 mb-0 font-weight-bold">{{ selectedTicket.hardware_metadata.directorQuote.clientApprovalUser }}</p>
                        </v-flex>
                        <v-flex xs12 sm4 class="mb-1" v-if="selectedTicket.hardware_metadata.directorQuote.clientApprovalFolio">
                          <small class="grey--text">Folio de Autorización</small>
                          <p class="body-2 mb-0 font-weight-bold">{{ selectedTicket.hardware_metadata.directorQuote.clientApprovalFolio }}</p>
                        </v-flex>
                        <v-flex xs12 class="mt-2" v-if="selectedTicket.hardware_metadata.directorQuote.clientNotes">
                          <small class="grey--text">Comentarios:</small>
                          <p class="body-2 mb-0 font-italic">"{{ selectedTicket.hardware_metadata.directorQuote.clientNotes }}"</p>
                        </v-flex>
                      </v-layout>
                    </div>

                    <!-- 3. Si fue rechazada -->
                    <div v-else-if="selectedTicket.hardware_metadata.directorQuote.clientStatus === 'rechazada'">
                      <div class="d-flex align-center justify-space-between flex-wrap mb-2">
                        <div class="d-flex align-center">
                          <v-icon color="error" class="mr-2">cancel</v-icon>
                          <h4 class="subheading font-weight-bold error--text mb-0">
                            Cotización Declinada
                          </h4>
                        </div>
                        <v-btn
                          small
                          color="success"
                          class="font-weight-bold text-none mt-1 mt-sm-0"
                          @click="openQuoteModal('aprobada')"
                        >
                          <v-icon left small>check_circle</v-icon>
                          Aprobar Cotización
                        </v-btn>
                      </div>
                      <p class="body-2 grey--text text--darken-2 mb-0 mt-2">
                        La cotización fue declinada. Motivo / Comentarios: 
                        <strong>"{{ selectedTicket.hardware_metadata.directorQuote.clientNotes || 'Sin comentarios' }}"</strong>
                      </p>
                    </div>
                  </div>
                </v-card>
              </div>

              <!-- Si no hay cotización requerida -->
              <div v-else class="text-xs-center py-6">
                <v-icon size="64" color="grey lighten-1" class="mb-3">request_quote</v-icon>
                <h3 class="title font-weight-bold grey--text text--darken-2 mb-1">Sin Cotización Pendiente</h3>
                <p class="body-2 grey--text mb-0" style="max-width: 460px; margin: 0 auto;">
                  Este reporte técnico no requiere cargos extraordinarios o la cotización formal se encuentra en preparación por el Director Técnico.
                </p>
              </div>
            </v-tab-item>

          </v-tabs-items>

          <!-- Si NO es ticket de hardware: mostramos directamente la Información General -->
          <div v-else>
            <v-card class="elevation-1 pa-4 mb-3 border-radius-8">
              <h4 class="subheading font-weight-bold mb-3 primary--text">
                <v-icon small color="primary" class="mr-1">assignment</v-icon>
                Descripción del Problema
              </h4>
              <p class="body-1 text-pre-wrap mb-0">{{ cleanTicketDescription(selectedTicket) }}</p>
            </v-card>

            <v-card class="elevation-1 pa-4 border-radius-8">
              <h4 class="subheading font-weight-bold mb-3 primary--text">
                <v-icon small color="primary" class="mr-1">info</v-icon>
                Detalles de Atención
              </h4>
              <v-layout row wrap>
                <v-flex xs12 sm6 class="mb-3">
                  <small class="grey--text">Responsable Técnico</small>
                  <p class="body-1 mb-0">{{ selectedTicket.responsable || 'Sin asignar' }}</p>
                </v-flex>
                <v-flex xs12 sm6 class="mb-3">
                  <small class="grey--text">Área Canalizada</small>
                  <p class="body-1 mb-0">{{ selectedTicket.area || 'General' }}</p>
                </v-flex>
                <v-flex xs12 sm6 class="mb-3" v-if="selectedTicket.client_email">
                  <small class="grey--text">Correo del Solicitante</small>
                  <p class="body-2 mb-0">{{ selectedTicket.client_email }}</p>
                </v-flex>
                <v-flex xs12 sm6 class="mb-3" v-if="selectedTicket.client_phone">
                  <small class="grey--text">Teléfono de Contacto</small>
                  <p class="body-2 mb-0">{{ selectedTicket.client_phone }}</p>
                </v-flex>
              </v-layout>
            </v-card>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn color="primary" flat @click="ticketDetailsDialog = false" class="text-none font-weight-bold">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══════════════════════════════════════════════════════════════════════ -->
    <!-- ── MODAL: RESPONDER / AUTORIZAR COTIZACIÓN ── -->
    <!-- ═══════════════════════════════════════════════════════════════════════ -->
    <v-dialog v-model="quoteResponseDialog" max-width="500">
      <v-card class="elevation-2 border-radius-8">
        <v-toolbar flat dark :color="quoteResponseType === 'aprobada' ? 'success' : 'error'">
          <v-icon left>{{ quoteResponseType === 'aprobada' ? 'check_circle' : 'cancel' }}</v-icon>
          <v-toolbar-title class="font-weight-bold">
            {{ quoteResponseType === 'aprobada' ? 'Autorizar Cotización' : 'Decline Cotización' }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="quoteResponseDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <p class="body-1 mb-3">
            {{ quoteResponseType === 'aprobada' 
              ? '¿Confirmas la autorización de los trabajos y costos desglosados en esta cotización?' 
              : '¿Deseas declinar esta cotización técnica?' }}
          </p>

          <div v-if="selectedTicket && selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.directorQuote" class="pa-3 mb-3 bg-light-gray border-radius-8">
            <div class="d-flex justify-space-between align-center">
              <span>Monto Total:</span>
              <strong class="title font-weight-bold" :class="quoteResponseType === 'aprobada' ? 'success--text' : 'error--text'">
                {{ formatCurrency(selectedTicket.hardware_metadata.directorQuote.total) }}
              </strong>
            </div>
          </div>

          <v-textarea
            v-model="quoteResponseNotes"
            label="Comentarios u observaciones (opcional)"
            placeholder="Escribe notas adicionales o instrucciones para el equipo técnico..."
            rows="3"
            outlined
            dense
          ></v-textarea>
        </v-card-text>

        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn flat @click="quoteResponseDialog = false" :disabled="submittingQuoteResponse">Cancelar</v-btn>
          <v-btn
            :color="quoteResponseType === 'aprobada' ? 'success' : 'error'"
            :loading="submittingQuoteResponse"
            class="font-weight-bold text-none"
            @click="confirmQuoteResponse"
          >
            {{ quoteResponseType === 'aprobada' ? 'Confirmar Autorización' : 'Confirmar Rechazo' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══════════════════════════════════════════════════════════════════════ -->
    <!-- ── MODAL: VISOR DE FOTOGRAFÍAS AMPLIADAS ── -->
    <!-- ═══════════════════════════════════════════════════════════════════════ -->
    <v-dialog v-model="previewImageDialog" max-width="800">
      <v-card class="elevation-2">
        <v-toolbar flat dark color="black" dense>
          <v-toolbar-title class="body-1 font-weight-bold">Evidencia Fotográfica en Sitio</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="previewImageDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-0 text-xs-center bg-black d-flex justify-center align-center" style="min-height: 400px; background: #121212;">
          <img :src="previewImageUrl" alt="Evidencia ampliada" style="max-width: 100%; max-height: 75vh; object-fit: contain;" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Success Dialog -->
    <v-dialog v-model="successDialog" max-width="400">
      <v-card class="elevation-2">
        <v-card-text class="text-center pa-6">
          <v-icon size="64" color="success" class="mb-4">check_circle</v-icon>
          <h3 class="headline font-weight-bold mb-2">¡Problema Resuelto!</h3>
          <p class="body-2 grey--text">Nos alegra haber podido ayudarte. Si tienes más preguntas, estamos aquí para ti.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" flat @click="successDialog = false; currentState = 'hub'">
            Volver al Centro de Ayuda
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Ticket Submitted Dialog -->
    <v-dialog v-model="ticketSubmittedDialog" max-width="400">
      <v-card class="elevation-2">
        <v-card-text class="text-center pa-6">
          <v-icon size="64" color="primary" class="mb-4">confirmation_number</v-icon>
          <h3 class="headline font-weight-bold mb-2">¡Ticket Creado!</h3>
          <p class="body-2 grey--text mb-4">Tu ticket ha sido enviado exitosamente. Guarda este folio para dar seguimiento.</p>
          <p class="caption" style="font-family: monospace; background: #f5f5f5; padding: 8px; border-radius: 4px;">
            Folio: {{ lastTicketFolio }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" flat @click="resetTicketForm(); ticketSubmittedDialog = false">
            Crear otro ticket
          </v-btn>
          <v-btn color="primary" @click="ticketSubmittedDialog = false; goToMyTickets()">
            Ver mis tickets
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
    import pdf from 'vue-pdf'
    import { mapGetters } from 'vuex'
    import { ticketsService } from '@/services/ticketsService'

    export default {
        name: "ayuda_admin",
        components: { pdf },
        data () {
            return {
                currentState: 'hub',
                hubTab: 'manuales',
                searchQuery: '',
                faqSearchQuery: '',
                filteredManuals: [],
                readingManual: null,
                pdfSrc: null,
                numPages: 0,
                loadingPdf: false,
                ticketForm: {
                    title: '',
                    description: '',
                    area_id: null,
                    client_name: '',
                    client_email: '',
                    client_phone: ''
                },
                relatedManuals: [],
                areas: [],
                tickets: [],
                selectedTicket: null,
                detailTab: 'tab-info',
                quoteResponseDialog: false,
                quoteResponseType: 'aprobada',
                quoteResponseNotes: '',
                submittingQuoteResponse: false,
                previewImageDialog: false,
                previewImageUrl: '',
                loadingAreas: false,
                loadingTickets: false,
                successDialog: false,
                ticketSubmittedDialog: false,
                ticketDetailsDialog: false,
                submittingTicket: false,
                lastTicketFolio: null,
                errores: {},
                errorAlert: '',
                faqs: [
                    {
                        icon: 'person_add',
                        question: '¿Cómo creo una invitación para un visitante?',
                        answer: 'Dirígete a la sección de Invitaciones, completa el formulario con los datos del visitante, especifica la fecha y hora de acceso, y envía la invitación. El visitante recibirá un código QR para escanear en la entrada.',
                        tips: 'Prepara los datos del visitante antes de crear la invitación. Asegúrate de incluir un número de teléfono válido para que reciba notificaciones.'
                    },
                    {
                        icon: 'lock_reset',
                        question: '¿Qué debo hacer si olvido mi contraseña?',
                        answer: 'En la pantalla de inicio de sesión, haz clic en "¿Olvidaste tu contraseña?". Ingresa tu correo electrónico y recibirás un enlace para restaurar tu contraseña.',
                        tips: 'Revisa tu bandeja de spam si no recibes el correo en 5 minutos. El enlace es válido por 24 horas.'
                    },
                    {
                        icon: 'local_parking',
                        question: '¿Cómo reservo un espacio de estacionamiento?',
                        answer: 'En la sección Parking, selecciona el área disponible, elige la fecha y hora de tu reserva, y confirma. Recibirás una confirmación con tu código de reserva.',
                        tips: 'Las reservas se pueden hacer con hasta 7 días de anticipación. Puedes modificar o cancelar tu reserva hasta 2 horas antes.'
                    },
                    {
                        icon: 'history',
                        question: '¿Cómo veo el historial de mis accesos?',
                        answer: 'Dirígete a tu perfil y selecciona "Historial de Accesos". Allí podrás ver todos tus movimientos registrados con fecha, hora y ubicación.',
                        tips: 'El historial se guarda durante 90 días. Puedes filtrar por fecha o tipo de acceso para encontrar registros específicos.'
                    },
                    {
                        icon: 'qr_code_scanner',
                        question: '¿Qué hago si no puedo escanear un código QR?',
                        answer: 'Asegúrate de que tu cámara está habilitada en los permisos de la aplicación. Si persiste el problema, reinicia la aplicación o contacta a soporte mediante un ticket.',
                        tips: 'Limpia la lente de tu cámara y asegúrate de que haya buena iluminación. Intenta acercarte más al código QR.'
                    },
                    {
                        icon: 'schedule',
                        question: '¿Cuál es el horario de atención del equipo de soporte?',
                        answer: 'Nuestro equipo de soporte atiende de lunes a viernes de 8:00 AM a 6:00 PM. Los tickets creados fuera de horario serán atendidos al siguiente día hábil.',
                        tips: 'Para problemas urgentes, usa la sección de contacto de emergencia en tu perfil.'
                    },
                    {
                        icon: 'edit',
                        question: '¿Cómo cambio mis datos de contacto?',
                        answer: 'En la sección de Perfil, edita tus datos de contacto (teléfono, correo), guarda los cambios y recibirás una confirmación de actualización.',
                        tips: 'Es importante mantener tu información actualizada para recibir notificaciones importantes y alertas de seguridad.'
                    },
                    {
                        icon: 'support_agent',
                        question: '¿Qué debo incluir en un ticket de soporte?',
                        answer: 'Incluye un título descriptivo, una descripción detallada del problema, qué intentaste, qué sucedió y cualquier mensaje de error que recibiste. Esto ayudará a nuestro equipo a resolver tu problema más rápidamente.',
                        tips: 'Adjunta capturas de pantalla si es posible. Proporciona información sobre el dispositivo y navegador que utilizas.'
                    }
                ]
            }
        },
        computed: {
            ...mapGetters(['getProfile']),
            isAdmin() {
                const profile = this.getProfile || {}
                return profile.roll === 5 || profile.is_superuser === true
            },
            hasTickets() {
                return this.tickets.length > 0
            },
            locationName() {
                const profile = this.getProfile || {}
                return profile.is_building ? 'edificio corporativo' : 'parque'
            },
            locationNameCapitalized() {
                const profile = this.getProfile || {}
                return profile.is_building ? 'Edificio Corporativo' : 'Parque Industrial'
            },
            filteredFaqs() {
                if (!this.faqSearchQuery.trim()) {
                    return this.faqs
                }
                const query = this.faqSearchQuery.toLowerCase()
                return this.faqs.filter(faq => {
                    const questionMatch = faq.question.toLowerCase().includes(query)
                    const answerMatch = faq.answer.toLowerCase().includes(query)
                    return questionMatch || answerMatch
                })
            },
            availableManuals() {
                if (!this.getProfile || !this.getProfile.roll) return []
                const roll = this.getProfile.roll
                const isSupervisor = this.getProfile.is_supervisor
                const isSuperuser = this.getProfile.is_superuser
                const isShipmentsAnalyst = this.getProfile.shipments_management
                const manuals = []

                if (roll === 5 || isSuperuser) {
                    manuals.push({
                        title: 'Administrador General',
                        description: 'Uso, configuración y gestión integral de la plataforma.',
                        icon: 'admin_panel_settings',
                        link: 'https://intrare-empresa-test.s3.us-east-2.amazonaws.com/manuales/ManualAdministrador21.pdf',
                        color: 'primary',
                        keywords: ['administración', 'general', 'configuración', 'plataforma', 'gestión']
                    })
                }
                if (roll === 4 || roll === 5 || isSuperuser) {
                    manuals.push({
                        title: `Administrador de ${this.locationNameCapitalized}`,
                        description: `Control, administración y monitoreo de empresas del ${this.locationName}.`,
                        icon: 'location_city',
                        link: 'https://intrare-empresa-test.s3.us-east-2.amazonaws.com/manuales/Manual+Usuario+++Administrador+de+Parque+Industrial.pdf',
                        color: 'indigo',
                        keywords: ['parque', 'industrial', 'empresas', 'monitoreo', 'control']
                    })
                }
                if (roll === 5 || isSuperuser || roll === 4) {
                    manuals.push({
                        title: 'Administrador de Parking',
                        description: 'Control y asignación de espacios, accesos y políticas de estacionamiento.',
                        icon: 'local_parking',
                        link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+USUARIO+PARKING.pdf',
                        color: 'blue darken-2',
                        keywords: ['parking', 'estacionamiento', 'espacios', 'accesos', 'reservación']
                    })
                }
                if (isSupervisor || isShipmentsAnalyst || roll === 5 || isSuperuser) {
                    manuals.push({
                        title: 'Supervisor de Embarques',
                        description: 'Gestión en piso, control de tiempos y liberación de cargas.',
                        icon: 'assignment_turned_in',
                        link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+USUARIO+SUPERVISOR+EMBARQUE.pdf',
                        color: 'orange darken-2',
                        keywords: ['embarques', 'supervisor', 'carga', 'tiempos', 'andén']
                    })
                    manuals.push({
                        title: 'Administrador de Embarques',
                        description: 'Configuración logística, asignación de andenes y tiempos de carga.',
                        icon: 'local_shipping',
                        link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+USUARIO+EMBARQUES.pdf',
                        color: 'deep-orange darken-1',
                        keywords: ['embarques', 'logística', 'andenes', 'carga', 'administrador']
                    })
                }
                if (roll === 2 || roll === 3 || roll === 5 || isSuperuser || roll === 4 || roll === 9) {
                    manuals.push({
                        title: 'Vigilante y Caseta',
                        description: 'Escaneo de QR, control de accesos, visitantes y vehículos.',
                        icon: 'security',
                        link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+VIGILANTE.pdf',
                        color: 'blue-grey darken-1',
                        keywords: ['vigilante', 'seguridad', 'qr', 'accesos', 'visitantes']
                    })
                }
                if (roll === 1 || roll === 5 || isSuperuser) {
                    manuals.push({
                        title: 'Usuario Empleado',
                        description: 'Creación de invitaciones, reservación de accesos y perfil personal.',
                        icon: 'badge',
                        link: 'https://intrare-empresa-test.s3.us-east-2.amazonaws.com/manuales/ManualEmpleado21.pdf',
                        color: 'teal darken-1',
                        keywords: ['empleado', 'invitaciones', 'accesos', 'perfil', 'reservación']
                    })
                }
                if (roll === 15 || roll === 11 || roll === 5 || isSuperuser) {
                    manuals.push({
                        title: 'Proveedores y Transportistas',
                        description: 'Pre-registro, agendamiento de visitas y acceso a instalaciones.',
                        icon: 'store',
                        link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+USUARIO+PROVEEDORES.pdf',
                        color: 'brown darken-1',
                        keywords: ['proveedor', 'transportista', 'visitas', 'registro', 'acceso']
                    })
                }

                return manuals
            }
        },
        watch: {
            getProfile: {
                handler(profile) {
                    if (profile && profile.email) {
                        this.filteredManuals = this.availableManuals
                        this.loadAreas()
                        this.loadTickets()
                    }
                },
                immediate: true
            },
            currentState() {
                this.$vuetify.goTo(0, { duration: 0 })
            }
        },
        mounted() {
            if (this.getProfile && this.getProfile.email) {
                this.loadTickets()
                this.loadAreas()
            }
        },
        methods: {
            loadAreas() {
                this.loadingAreas = true
                ticketsService.getAreas().then(response => {
                    if (response && response.success) {
                        this.areas = response.data || []
                    }
                }).catch(err => {
                    console.error('Error cargando áreas:', err)
                }).finally(() => {
                    this.loadingAreas = false
                })
            },
            loadTickets() {
                const email = (this.getProfile && this.getProfile.email) ? this.getProfile.email.trim() : ''
                if (!email) {
                    this.tickets = []
                    return
                }
                this.loadingTickets = true
                ticketsService.getTicketsByEmail(email, 100).then(response => {
                    if (response && response.success) {
                        this.tickets = response.data || []
                    }
                }).catch(err => {
                    console.error('Error cargando tickets:', err)
                }).finally(() => {
                    this.loadingTickets = false
                })
            },
            filterManuals() {
                if (!this.searchQuery.trim()) {
                    this.filteredManuals = this.availableManuals
                    return
                }
                const query = this.searchQuery.toLowerCase()
                this.filteredManuals = this.availableManuals.filter(manual => {
                    const titleMatch = manual.title.toLowerCase().includes(query)
                    const descMatch = manual.description.toLowerCase().includes(query)
                    const keywords = manual.keywords || []
                    const keywordMatch = keywords.some(kw => kw.includes(query))
                    return titleMatch || descMatch || keywordMatch
                })
            },
            startReadingManual(manual) {
                this.readingManual = manual
                this.pdfSrc = null
                this.numPages = 0
                this.loadingPdf = false
                this.currentState = 'reading-manual'

                if (manual.link && manual.link !== '#') {
                    this.loadingPdf = true
                    setTimeout(() => {
                        const task = pdf.createLoadingTask(manual.link)
                        this.pdfSrc = task
                        task.promise.then(pdfObj => {
                            this.numPages = pdfObj.numPages
                            this.loadingPdf = false
                        }).catch(err => {
                            console.error('PDF error', err)
                            this.loadingPdf = false
                        })
                    }, 350)
                }
            },
            printPdf() {
                if (this.$refs.myPdfComponent) {
                    this.$refs.myPdfComponent.print()
                }
            },
            resolvedProblem() {
                this.successDialog = true
            },
            updateRelatedManuals() {
                const text = (this.ticketForm.title + ' ' + this.ticketForm.description).toLowerCase()
                if (text.length < 3) {
                    this.relatedManuals = []
                    return
                }

                this.relatedManuals = this.availableManuals.filter(manual => {
                    const keywords = manual.keywords || []
                    const hasMatch = keywords.some(kw => text.includes(kw))
                    return hasMatch
                }).slice(0, 5)
            },
            goToCreateTicket() {
                const profile = this.getProfile || {}
                this.ticketForm.client_name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
                this.ticketForm.client_email = profile.email || ''
                this.ticketForm.client_phone = profile.celular || ''
                this.errores = {}
                this.errorAlert = ''
                this.currentState = 'create-ticket'
            },
            goToMyTickets() {
                this.currentState = 'my-tickets'
                this.loadTickets()
            },
            async submitTicket() {
                this.errores = {}
                this.errorAlert = ''

                let hasErrors = false

                if (!this.ticketForm.title || !this.ticketForm.title.trim()) {
                    this.errores.title = ['El título del problema es obligatorio']
                    hasErrors = true
                }

                if (!this.ticketForm.description || !this.ticketForm.description.trim()) {
                    this.errores.description = ['La descripción detallada es obligatoria']
                    hasErrors = true
                }

                if (!this.ticketForm.area_id) {
                    this.errores.area_id = ['Debes seleccionar el área correspondiente']
                    hasErrors = true
                }

                if (hasErrors) {
                    this.errorAlert = 'Por favor completa todos los campos obligatorios (*) antes de enviar.'
                    return
                }

                this.submittingTicket = true

                try {
                    const profile = this.getProfile || {}
                    const companyName = profile.name_company || (profile.empresa && profile.empresa.name) || profile.empresa_name || ''

                    const payload = {
                        title: this.ticketForm.title,
                        description: this.ticketForm.description || '',
                        area_id: this.ticketForm.area_id || null,
                        client_name: this.ticketForm.client_name || '',
                        client_email: this.ticketForm.client_email || '',
                        client_phone: this.ticketForm.client_phone || '',
                        client_company: companyName
                    }

                    const response = await ticketsService.createTicket(payload)

                    this.lastTicketFolio = response.data.folio
                    this.ticketSubmittedDialog = true
                    this.loadTickets()
                } catch (error) {
                    console.error('Error submitting ticket:', error)
                    if (error.response && error.response.data && error.response.data.message) {
                        this.errorAlert = error.response.data.message
                    } else {
                        this.errorAlert = 'Error al crear el ticket. Por favor, intenta nuevamente.'
                    }
                } finally {
                    this.submittingTicket = false
                }
            },
            openTicketDetails(ticket) {
                this.loadingTickets = true
                this.detailTab = 'tab-info'
                ticketsService.getTicketById(ticket.id).then(response => {
                    if (response.success) {
                        this.selectedTicket = response.data
                        this.ticketDetailsDialog = true
                    }
                }).finally(() => {
                    this.loadingTickets = false
                })
            },
            openQuoteModal(action) {
                this.quoteResponseType = action
                this.quoteResponseNotes = ''
                this.quoteResponseDialog = true
            },
            async confirmQuoteResponse() {
                if (!this.selectedTicket) return
                this.submittingQuoteResponse = true
                const profile = this.getProfile || {}
                const userFullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.username || 'Administrador de Empresa'
                const payload = {
                    status: this.quoteResponseType,
                    approved_by_user: userFullName,
                    notes: this.quoteResponseNotes,
                    system_origin: 'Empresa'
                }
                const res = await ticketsService.respondQuote(this.selectedTicket.id, payload)
                this.submittingQuoteResponse = false
                if (res && res.success) {
                    this.quoteResponseDialog = false
                    this.selectedTicket = res.data
                    this.loadTickets()
                }
            },
            previewPhoto(url) {
                this.previewImageUrl = url
                this.previewImageDialog = true
            },
            cleanTicketDescription(ticket) {
                if (!ticket) return ''
                if (ticket.clean_description) return ticket.clean_description
                return this.cleanHtml(ticket.description)
            },
            formatCurrency(val) {
                const num = parseFloat(val) || 0
                return '$' + num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' MXN'
            },
            getEquipmentConditionColor(condition) {
                const map = {
                    'excelente': '#4CAF50',
                    'bueno': '#8BC34A',
                    'regular': '#FF9800',
                    'critico': '#F44336',
                    'fuera_servicio': '#D32F2F'
                }
                return map[condition] || '#2196F3'
            },
            getQuoteStatusColor(status) {
                const map = {
                    'pendiente_cliente': '#FF9800',
                    'aprobada': '#4CAF50',
                    'rechazada': '#F44336'
                }
                return map[status] || '#757575'
            },
            getQuoteStatusIcon(status) {
                const map = {
                    'pendiente_cliente': 'hourglass_top',
                    'aprobada': 'verified',
                    'rechazada': 'cancel'
                }
                return map[status] || 'help_outline'
            },
            getQuoteStatusLabel(status) {
                const map = {
                    'pendiente_cliente': 'Pendiente de Autorización',
                    'aprobada': 'Cotización Autorizada',
                    'rechazada': 'Cotización Declinada'
                }
                return map[status] || status
            },
            isAdditionalChargeRequired() {
                if (!this.selectedTicket || !this.selectedTicket.hardware_metadata) return false
                const meta = this.selectedTicket.hardware_metadata
                if (meta.technicalReview && meta.technicalReview.needsQuote) return true
                if (meta.hasAdditionalCharge) return true
                if (meta.directorQuote) return true
                return false
            },
            getStatusColor(status) {
                const colorMap = {
                    'Nuevo': '#FF9800',
                    'En progreso': '#2196F3',
                    'Resuelto': '#4CAF50',
                    'Cerrado': '#9E9E9E'
                }
                return colorMap[status] || '#757575'
            },
            formatDate(dateString) {
                if (!dateString) return '—'
                const date = new Date(dateString)
                return date.toLocaleDateString('es-MX', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                })
            },
            cleanHtml(html) {
                if (!html) return '—'
                return html.replace(/<[^>]*>/g, '').trim() || '—'
            },
            resetTicketForm() {
                const profile = this.getProfile || {}
                this.ticketForm = {
                    title: '',
                    description: '',
                    area_id: null,
                    client_name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
                    client_email: profile.email || '',
                    client_phone: profile.celular || ''
                }
                this.relatedManuals = []
                this.errores = {}
                this.errorAlert = ''
                this.currentState = 'create-ticket'
            },
            previewPhoto(img) {
                this.previewImageUrl = img
                this.previewImageDialog = true
            }
        }
    }
</script>

<style scoped>
.help-center-container {
  min-height: 100vh;
  padding: 24px 0;
}

/* Help Hub State */
.help-hub {
  max-width: 1200px;
  margin: 0 auto;
}

.help-header {
  text-align: center;
  background: linear-gradient(135deg, #002b48 0%, #004a6e 100%);
  color: white;
  padding: 48px 24px;
  border-radius: 24px;
  margin-bottom: 40px;
  box-shadow: 0 8px 16px rgba(0, 43, 72, 0.15);
}

.help-header h1 {
  margin-bottom: 8px;
  color: white !important;
  font-size: 2.5rem;
  letter-spacing: -0.5px;
}

.help-header p {
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.05rem;
  letter-spacing: 0.3px;
}

/* Tab Content */
.tab-content {
  background: transparent !important;
  padding: 20px !important;
}

.manual-list-container {
  background: transparent !important;
  padding: 0 !important;
}

.manual-list-item {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  margin-bottom: 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  border-left: 4px solid transparent;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.08);
  overflow: hidden;
  padding: 12px !important;
}

.manual-list-item:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%);
  border-left-color: #2196F3;
  transform: translateX(4px);
  box-shadow: 0 4px 16px rgba(33, 150, 243, 0.15);
}

.related-manuals-title {
  color: #2196F3;
}

/* Tabs Styling */
.help-tabs {
  background: white !important;
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.help-tabs ::v-deep .v-tabs__bar {
  border-radius: 12px;
  background: transparent !important;
}

.help-tabs ::v-deep .v-tab {
  color: #666 !important;
  font-size: 0.95rem !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  min-width: 150px;
  transition: all 0.3s ease;
}

.help-tabs ::v-deep .v-tab:hover {
  color: #2196F3 !important;
}

.help-tabs ::v-deep .v-tab--active {
  color: #2196F3 !important;
}

.tab-label {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px;
}

/* FAQ Section */
.faq-list-container ::v-deep .v-list__group__header {
  border-radius: 12px;
}

.faq-list-item {
  padding: 0 !important;
}

.faq-list-item ::v-deep .v-list__group__header .v-list__tile {
  border-radius: 12px;
}

.faq-list-item ::v-deep .v-list__group__header__append-icon .v-icon {
  color: #2196F3 !important;
}

.faq-list-item ::v-deep .v-list__group__items .v-list__tile {
  height: auto !important;
  min-height: 0;
  white-space: normal;
  padding: 0 16px 20px 72px;
  align-items: flex-start;
}

.faq-list-item ::v-deep .v-list__group__items .v-list__tile__content {
  align-items: flex-start;
}

.faq-answer-text {
  white-space: normal;
  color: #555;
  line-height: 1.7;
  font-size: 0.95rem;
}

.faq-tips {
  width: 100%;
  background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%);
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid #2196F3;
}

.faq-tips-label {
  display: inline-flex;
  align-items: center;
  color: #1976d2;
}

.faq-tips .caption {
  white-space: normal;
  color: #555;
  line-height: 1.5;
}

/* Reading Manual State */
.reading-state {
  max-width: 1000px;
  margin: 0 auto;
}

.pdf-viewer-container {
  min-height: 600px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 12px;
  padding: 40px 20px;
  margin: 20px 0;
}

.loading-state,
.not-available-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
}

.pdf-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.resolution-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

/* Create Ticket State */
.create-ticket-state {
  max-width: 1200px;
  margin: 0 auto;
}

.related-manual-item {
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 6px;
  margin-bottom: 4px;
}

.related-manual-item:hover {
  background: rgba(33, 150, 243, 0.1);
}

/* My Tickets State */
.my-tickets-state {
  max-width: 1000px;
  margin: 0 auto;
}

.ticket-list-item {
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 12px;
  border-radius: 8px;
  background: #f9f9f9;
  border-left: 4px solid transparent;
}

.ticket-list-item:hover {
  background: #eeeeee;
  border-left-color: #2196F3;
  transform: translateX(4px);
}

/* Tab Items */
.help-tabs ::v-deep .v-tabs__items {
  background: transparent !important;
  padding-top: 20px;
}

.help-tabs ::v-deep .v-window__container {
  background: transparent !important;
}

.help-tabs ::v-deep .v-window-item {
  background: transparent !important;
}

/* PDF page wrapper */
.pdf-page-wrapper {
  width: 100%;
  max-width: 860px;
  margin-bottom: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  background: white;
  overflow: visible;
}

.pdf-page-wrapper canvas {
  width: 100% !important;
  height: auto !important;
  display: block;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .help-header {
    padding: 30px 16px;
  }

  .help-header h1 {
    font-size: 1.75rem !important;
  }

  .resolution-buttons {
    flex-direction: column;
  }

  .resolution-buttons button {
    width: 100% !important;
  }
}

/* ── Rich Ticket Detail Modal Styles ── */
.ticket-detail-modal-card {
  border-radius: 12px;
  overflow: hidden;
}

.ticket-modal-toolbar {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.ticket-hero-banner {
  background: linear-gradient(135deg, #f0f7ff 0%, #e3f2fd 100%);
  border-bottom: 1px solid #bbdefb;
}

.ticket-hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  color: #546e7a;
  font-size: 0.9rem;
}

.meta-item {
  display: inline-flex;
  align-items: center;
}

.detail-modal-tabs {
  background: #002b48 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.detail-modal-tabs ::v-deep .v-tabs__bar {
  background: #002b48 !important;
}

.detail-modal-tabs ::v-deep .v-tab,
.tab-item-white {
  color: #ffffff !important;
  font-size: 0.92rem !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  opacity: 0.9 !important;
  transition: all 0.2s ease;
}

.detail-modal-tabs ::v-deep .v-tab:hover,
.detail-modal-tabs ::v-deep .v-tab--active {
  color: #ffffff !important;
  opacity: 1 !important;
  font-weight: 700 !important;
}

.detail-modal-tabs ::v-deep .v-tab .v-icon,
.tab-item-white .v-icon {
  color: #ffffff !important;
}

.detail-modal-tabs ::v-deep .v-tabs__slider {
  background-color: #2196F3 !important;
  height: 3px !important;
}

.bg-light-gray {
  background-color: #f8fafc;
}

.border-radius-8 {
  border-radius: 8px !important;
}

.text-pre-wrap {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
}

.msg-count-chip {
  font-size: 0.8rem;
  color: #1976d2;
  font-weight: bold;
}

/* Hardware Card */
.hw-card {
  border: 1px solid #ccfbf1;
  background: #f0fdfa !important;
}

.policy-notice-box {
  border-radius: 6px;
}

.policy-notice-box.with-charge {
  background: #fff7ed;
  border: 1px solid #ffedd5;
}

.policy-notice-box.no-charge {
  background: #f0fdf4;
  border: 1px solid #dcfce7;
}

/* Technical Review Section */
.findings-box {
  background: #f1f5f9;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.costs-strip {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.cost-col {
  padding: 4px 12px;
}

.evidence-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 12px;
  margin-top: 8px;
}

.evidence-thumb-wrap {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid #e2e8f0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.evidence-thumb-wrap:hover {
  transform: scale(1.04);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  border-color: #0f766e;
}

.evidence-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.evidence-thumb-wrap:hover .thumb-overlay {
  opacity: 1;
}

.tech-notes-card {
  background: #fafafa;
  border: 1px dashed #cfd8dc;
  border-radius: 6px;
}

/* Quote Section */
.quote-table-wrapper {
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.quote-table {
  width: 100%;
  border-collapse: collapse;
}

.quote-table th {
  background: #f1f5f9;
  color: #475569;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 10px 14px;
  text-align: left;
  border-bottom: 1px solid #cbd5e1;
}

.quote-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.9rem;
}

.quote-totals-card {
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.quote-approval-card {
  border-radius: 8px;
}

.quote-approval-card.pendiente_cliente {
  background: #fffbeb;
  border: 1px solid #fef3c7;
}

.quote-approval-card.aprobada {
  background: #f0fdf4;
  border: 1px solid #dcfce7;
}

.quote-approval-card.rechazada {
  background: #fef2f2;
  border: 1px solid #fee2e2;
}

</style>
